import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, Loader2 } from "lucide-react";
import { INDIAN_CITIES } from "@/data/cities";

// Photon (Komoot / OpenStreetMap) — free, worldwide, no key required.
const PHOTON_URL = "https://photon.komoot.io/api";

// Format one feature into "City, State, Country".
function formatFeature(f) {
  const p = f?.properties || {};
  const parts = [];
  const city = p.name || p.city || p.locality || p.town || p.village;
  if (city) parts.push(city);
  if (p.state && p.state !== city) parts.push(p.state);
  if (p.country && p.country !== p.state) parts.push(p.country);
  return parts.join(", ");
}

/**
 * City/place picker with LIVE search (Photon API on every keystroke, debounced 250ms).
 * Returns "City, State, Country" strings for the parent form.
 * When query is empty we show a curated Indian-city list so mobile users can
 * simply pick without typing.
 */
export default function CityPicker({
  value,
  onChange,
  placeholder = "Search city, state, country",
  testId = "city-picker",
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Curated fallback when nothing is typed
  const fallback = useMemo(() => INDIAN_CITIES.slice(0, 60), []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Debounced live search
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        const url =
          `${PHOTON_URL}/?q=${encodeURIComponent(q)}&limit=10&lang=en`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const feats = (data?.features || [])
          .filter((f) => {
            const t = f?.properties?.type || "";
            const osmVal = f?.properties?.osm_value || "";
            // keep only place-type results (cities/towns/villages/districts)
            return (
              [
                "city",
                "town",
                "village",
                "hamlet",
                "county",
                "state",
                "district",
                "region",
                "municipality",
                "locality",
                "administrative",
              ].includes(t) ||
              [
                "city",
                "town",
                "village",
                "hamlet",
                "county",
                "district",
                "administrative",
              ].includes(osmVal)
            );
          })
          .map(formatFeature)
          .filter(Boolean);
        // de-duplicate keeping order
        const seen = new Set();
        const uniq = feats.filter((s) => {
          const k = s.toLowerCase();
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setResults(uniq);
      } catch (e) {
        if (e.name !== "AbortError") {
          // fallback: locally-filter our curated Indian list
          const ql = q.toLowerCase();
          setResults(
            INDIAN_CITIES.filter((c) => c.toLowerCase().includes(ql)).slice(
              0,
              10
            )
          );
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, open]);

  const pick = (city) => {
    onChange(city);
    setCustom(false);
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  if (custom) {
    return (
      <div className="flex gap-2">
        <input
          className="input flex-1"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your city, state, country"
          required={required}
          data-testid={`${testId}-custom`}
        />
        <button
          type="button"
          onClick={() => {
            setCustom(false);
            setOpen(true);
            onChange("");
          }}
          className="btn-ghost text-xs px-3 py-2"
          data-testid={`${testId}-back`}
        >
          Search
        </button>
      </div>
    );
  }

  const list = query.trim() ? results : fallback;

  return (
    <div className="relative" ref={rootRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="input w-full flex items-center justify-between text-left"
        data-testid={`${testId}-trigger`}
      >
        <span
          className={value ? "" : "opacity-60"}
          style={{ color: value ? "var(--cream)" : "var(--gold-soft)" }}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          size={16}
          className="text-[color:var(--gold)] shrink-0"
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 200ms",
          }}
        />
      </button>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-2 rounded-xl overflow-hidden"
          style={{
            background: "rgba(24, 4, 8, 0.98)",
            border: "1px solid rgba(212,166,74,0.4)",
            boxShadow: "0 20px 50px -20px rgba(0,0,0,0.7)",
          }}
        >
          <div
            className="flex items-center gap-2 px-3 py-2 border-b"
            style={{ borderColor: "rgba(212,166,74,0.25)" }}
          >
            {loading ? (
              <Loader2
                size={14}
                className="text-[color:var(--gold)] animate-spin"
              />
            ) : (
              <Search size={14} className="text-[color:var(--gold-soft)]" />
            )}
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Start typing any city worldwide…"
              className="bg-transparent w-full text-sm outline-none text-[color:var(--cream)] font-sans-ui"
              data-testid={`${testId}-search`}
            />
          </div>
          <ul className="max-h-64 overflow-auto py-1">
            {list.map((c) => {
              const active = c === value;
              return (
                <li key={c}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className={`w-full text-left px-3 py-2 text-sm font-sans-ui flex items-center justify-between hover:bg-[rgba(212,166,74,0.1)] ${
                      active
                        ? "text-[color:var(--gold)]"
                        : "text-[color:var(--cream)]"
                    }`}
                    data-testid={`${testId}-opt-${c
                      .toLowerCase()
                      .replace(/[,\s]+/g, "-")}`}
                  >
                    <span className="truncate pr-2">{c}</span>
                    {active && <Check size={14} className="shrink-0" />}
                  </button>
                </li>
              );
            })}
            {!loading && list.length === 0 && query.trim() && (
              <li className="px-3 py-3 text-sm text-[color:var(--gold-soft)] opacity-80">
                No matches — try a fuller spelling, or use &quot;Other&quot; below.
              </li>
            )}
          </ul>
          <button
            type="button"
            onClick={() => {
              setCustom(true);
              setOpen(false);
              setQuery("");
              setResults([]);
            }}
            className="w-full text-center px-3 py-2 border-t text-sm font-sans-ui text-[color:var(--gold)]"
            style={{
              borderColor: "rgba(212,166,74,0.25)",
              background: "rgba(212,166,74,0.06)",
            }}
            data-testid={`${testId}-other`}
          >
            + Type manually (Other city / country)
          </button>
        </div>
      )}
    </div>
  );
}
